import React, { createContext } from "react";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { Asset_ExistsResult } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/asset_pb";
import {
  Contract,
  GetContractsByOrgResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { GetSubscriptionResponse } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  GetOrgInspectionQuotaResponse,
  GetOrganisationUserListResponse,
  Pagination,
  Role,
  RoleDetails,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { GetSubscriptionSeatUsageResponse } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_seats_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

// Users list tab information and filters
type OrgUsersListContextType = {
  orgUsersList: OrgUsersList;
  setOrgUsersList: React.Dispatch<React.SetStateAction<OrgUsersList>>;
};

type AdminOnlyFilterContextType = {
  adminOnlyFilter: boolean;
  setAdminOnlyFilter: React.Dispatch<React.SetStateAction<boolean>>;
};

type UserQueryFilterContextType = {
  userQueryFilter: string;
  setUserQueryFilter: React.Dispatch<React.SetStateAction<string>>;
};

type UserStatusFilterContextType = {
  userStatusFilter: UserStatus;
  setUserStatusFilter: React.Dispatch<React.SetStateAction<UserStatus>>;
};

type UserSeatTypeFilterContextType = {
  userSeatTypeFilter: SubscriptionSeatTypes;
  setUserSeatTypeFilter: React.Dispatch<
    React.SetStateAction<SubscriptionSeatTypes>
  >;
};

export type OrgUsersList = {
  usersListResponse: GetOrganisationUserListResponse;
  pagination: Pagination;
  isLoading: boolean;
};

export const OrgUsersListContext = createContext<OrgUsersListContextType>(
  {} as OrgUsersListContextType,
);

export const AdminOnlyFilterContext = createContext<AdminOnlyFilterContextType>(
  {} as AdminOnlyFilterContextType,
);

export const UserQueryFilterContext = createContext<UserQueryFilterContextType>(
  {} as UserQueryFilterContextType,
);

export const UserStatusFilterContext =
  createContext<UserStatusFilterContextType>({} as UserStatusFilterContextType);

export const UserSeatTypeFilterContext =
  createContext<UserSeatTypeFilterContextType>(
    {} as UserSeatTypeFilterContextType,
  );

// Main organisation information
type OrgDataContextType = {
  orgData: Role;
  setOrgData: React.Dispatch<React.SetStateAction<Role>>;
};

// New main organisation information with more details
type OrgDetailsContextType = {
  orgDetails: RoleDetails;
  setOrgDetails: React.Dispatch<React.SetStateAction<RoleDetails>>;
};

// New subscription data context
type SubDataContextType = {
  subData: GetSubscriptionResponse;
  setSubData: React.Dispatch<React.SetStateAction<GetSubscriptionResponse>>;
};

type OrgInspectionQuotaContextType = {
  orgInspectionQuota: GetOrgInspectionQuotaResponse;
  setOrgInspectionQuota: React.Dispatch<
    React.SetStateAction<GetOrgInspectionQuotaResponse>
  >;
};

type ContractsListContextType = {
  contractsList: GetContractsByOrgResponse;
  setContractsList: React.Dispatch<
    React.SetStateAction<GetContractsByOrgResponse>
  >;
  getCurrentContract: (
    contractsList: GetContractsByOrgResponse,
  ) => Contract | undefined;
  setIsContractListLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isContractListLoading: boolean;
  // Set when the contracts fetch fails or is skipped, so callers can distinguish "no contracts
  // fetched yet/failed" from "org genuinely has none" - contractsList.contracts is [] in both cases.
  contractListError: boolean;
  setContractListError: React.Dispatch<React.SetStateAction<boolean>>;
};

type OrgHasSensorsContextType = {
  orgHasSensors: Asset_ExistsResult;
  setOrgHasSensors: React.Dispatch<React.SetStateAction<Asset_ExistsResult>>;
};

type IsOrgAndSeatDataLoadingContextType = {
  isOrgAndSeatDataLoading: boolean;
  setIsOrgAndSeatDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

type SubscriptionSeatUsageContextType = {
  subscriptionSeatUsage: GetSubscriptionSeatUsageResponse;
  setSubscriptionSeatUsage: React.Dispatch<
    React.SetStateAction<GetSubscriptionSeatUsageResponse>
  >;
};

export const OrgDataContext = createContext<OrgDataContextType>(
  {} as OrgDataContextType,
);

export const OrgDetailsContext = createContext<OrgDetailsContextType>(
  {} as OrgDetailsContextType,
);

export const SubDataContext = createContext<SubDataContextType>(
  {} as SubDataContextType,
);

export const ContractsListContext = createContext<ContractsListContextType>(
  {} as ContractsListContextType,
);

export const OrgHasSensorsContext = createContext<OrgHasSensorsContextType>(
  {} as OrgHasSensorsContextType,
);

export const IsOrgAndSeatDataLoadingContext =
  createContext<IsOrgAndSeatDataLoadingContextType>(
    {} as IsOrgAndSeatDataLoadingContextType,
  );

export const SubscriptionSeatUsageContext =
  createContext<SubscriptionSeatUsageContextType>(
    {} as SubscriptionSeatUsageContextType,
  );

export const OrgInspectionQuotaContext =
  createContext<OrgInspectionQuotaContextType>(
    {} as OrgInspectionQuotaContextType,
  );
